/**
 * Copying to the system clipboard.
 *
 * `navigator.clipboard` is the real path and is what every supported browser
 * takes: the app is served over https in both environments, and localhost
 * counts as a secure context too. The textarea fallback is for the cases that
 * bypass all of that — a browser that blocks the async API outright, or a
 * rejected permission — where silently copying nothing is the worst outcome,
 * because the practitioner only finds out when they paste.
 *
 * Resolves true when the text made it to the clipboard, so a caller can hold
 * back its "Copied!" feedback if it did not.
 */
export function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard
      .writeText(text)
      .then(() => true)
      .catch(() => fallbackCopy(text));
  }
  return Promise.resolve(fallbackCopy(text));
}

function fallbackCopy(text: string): boolean {
  const el = document.createElement('textarea');
  el.value = text;
  // Off-screen but still focusable: a display:none or detached node cannot be
  // selected, and execCommand copies the selection.
  el.style.position = 'fixed';
  el.style.opacity = '0';
  document.body.appendChild(el);
  el.select();
  let copied = false;
  try {
    copied = document.execCommand('copy');
  } catch (_) {
    copied = false;
  }
  document.body.removeChild(el);
  return copied;
}
